import { css } from "@emotion/css";
import React from "react";
import {
  Button,
  OverlayArrow,
  Tooltip as AriaTooltip,
  TooltipProps,
  TooltipTrigger,
} from "react-aria-components";

import { slide } from "../../utils/GlobalAnimations";
import { _OverlayArrow } from "../_assets/_OverlayArrow";
import { useGetAllFocusShadows } from "../utils";

interface HoverInfoProps extends Omit<TooltipProps, "children"> {
  infoContent?: React.ReactNode;
  trigger: React.ReactNode;
  testID: string;
}

export function HoverInfo(props: HoverInfoProps) {
  const { trigger, infoContent, ...rest } = props;

  const getAllFocusShadows = useGetAllFocusShadows();

  return (
    <TooltipTrigger delay={0} closeDelay={0}>
      <Button
        className={css(styles.nonStyled, {
          "&[data-focus-visible]": getAllFocusShadows(),
        })}
      >
        {trigger}
      </Button>
      <AriaTooltip
        placement={props.placement}
        className={styles.tooltip}
        {...rest}
      >
        <OverlayArrow className={styles.overlayArrow}>
          <_OverlayArrow {...rest} />
        </OverlayArrow>
        {infoContent}
      </AriaTooltip>
    </TooltipTrigger>
  );
}

const styles = {
  tooltip: css({
    forcedColorAdjust: "none",
    outline: "none",
    backgroundColor: "var(--background-base)",
    color: "var(--text-base)",
    padding: "var(--space-2) var(--space-4)",
    borderRadius: "var(--border-radius-2)",
    boxShadow: "var(--shadow-menu)",
    maxWidth: "300px",
    "& *": {
      overflowWrap: "break-word",
    },

    "&[data-placement=top]": {
      marginBottom: "var(--space-2)",
      "--origin": "translateY(4px)",
    },

    "&[data-placement=bottom]": {
      marginTop: "var(--space-2)",
      "--origin": "translateY(-4px)",
      svg: {
        transform: "rotate(180deg) translateY(20px)",
      },
    },

    "&[data-placement=right]": {
      marginLeft: "var(--space-2)",
      "--origin": "translateX(-4px)",
      svg: {
        transform: "rotate(90deg) translate(10px)",
      },
    },

    "&[data-placement=left]": {
      marginRight: "var(--space-2)",
      "--origin": "translateX(4px)",
      svg: {
        transform: "rotate(-90deg) translate(10px)",
      },
    },

    "&[data-entering]": {
      animation: `${slide} 100ms`,
    },

    "&[data-exiting]": {
      animation: `${slide} 100ms reverse ease-in`,
    },
  }),
  nonStyled: css({
    padding: "0",
    border: "none",
    background: "none",
    margin: "0",
    cursor: "pointer",
    outline: "none",
  }),
  overlayArrow: css({
    "> svg &[data-placement=top]": {
      transform: "rotate(180deg)",
    },
  }),
};
